import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { fetchDomesByHost, remoteLogoutTablet } from "../../api/dome.api";
import { fetchHostById } from "../../api/host.api";
import Swal from "sweetalert2";

const HostDomes = () => {
  const location = useLocation();
  const [host, setHost] = useState(location.state?.hostData || null); // Initialize state with location data or null

  const { background } = useContext(ThemeContext);
  const isDark = background === "dark";

  const { id: hostId } = useParams();
  const navigate = useNavigate();

  const [domes, setDomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(9);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  // Fetch Host Data if not available
  useEffect(() => {
    const loadHost = async () => {
      if (!host && hostId) {
        try {
          const response = await fetchHostById(hostId);
          setHost(response?.data || response); // Adjust based on API response structure
        } catch (error) {
          console.error("Error fetching host details:", error);
          // Handle error appropriately, maybe redirect or show error message
        }
      }
    };
    loadHost();
  }, [host, hostId]);


  /* ----------------------------- Fetch Domes ----------------------------- */
  const loadDomes = async (pageNumber = 1, query = "") => {
    try {
      if (pageNumber === 1) {
        if (query) setIsSearching(true);
        else setLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const data = await fetchDomesByHost(hostId, query, pageNumber, limit);
      if (pageNumber === 1) setDomes(data.domes || data);
      else setDomes((prev) => [...prev, ...(data.domes || data)]);

      setHasMore(data.hasNextPage || (data.domes?.length || 0) === limit);
    } catch (error) {
      console.error("Error fetching domes:", error);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      setIsSearching(false);
    }
  };

  // Debounce search effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    loadDomes(1, debouncedSearch);
  }, [debouncedSearch, hostId]);

  /* ----------------------------- Handlers ----------------------------- */
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
  };

  const handleDomeClick = (domeId) => {
    navigate(`${domeId}`);
  };

  const navigateToCreatePage = () => {
    if (!host) return;
    navigate("create", {
      state: {
        hostData: host,
      },
    });
  };

  const loadNextPage = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadDomes(nextPage, search);
  };

  const handleLogoutTablet = async (e, domeId, domeName) => {
    e.stopPropagation(); // Prevent card click

    const result = await Swal.fire({
      title: "Disconnect Tablet?",
      text: `This will logout the tablet from ${domeName}. The tablet will need to be re-registered to work again.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Disconnect",
    });

    if (result.isConfirmed) {
      try {
        await remoteLogoutTablet(domeId);
        // Update local state to hide the button immediately
        setDomes((prevDomes) =>
          prevDomes.map((d) =>
            d._id === domeId ? { ...d, isTabletRegistered: false } : d
          )
        );
        Swal.fire("Logged Out", "The tablet has been disconnected successfully.", "success");
      } catch (error) {
        Swal.fire("Error", "No registered tablet found for this dome or failed to disconnect.", "error");
      }
    }
  };

  /* ----------------------------- Loading State ----------------------------- */
  if (loading && domes.length === 0)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  /* ----------------------------- Error/No Host State ----------------------------- */
  if (!host) {
    return <div className="text-center py-5">Loading Host Data...</div>
  }


  /* ----------------------------- Render ----------------------------- */
  return (
    <div className="container-fluid">
      {/* Header + Search */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex align-items-center">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-outline-secondary me-3"
          >
            <i className="fas fa-arrow-left me-2"></i>Back
          </button>
          <h4 className="fs-20 mb-0">Domes for Host: {host.companyName || host.hostDisplayName}</h4>
        </div>
        <form onSubmit={handleFormSubmit} className="d-flex gap-2 flex-grow-1 flex-md-grow-0 w-100 w-md-auto">
          <input
            type="text"
            placeholder="Search domes..."
            value={search}
            onChange={handleSearchChange}
            className="form-control"
            style={{ borderRadius: "5px", fontSize: "14px", flexGrow: 1, maxWidth: "250px" }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={navigateToCreatePage}
            style={{ fontSize: "14px", borderRadius: "5px", whiteSpace: 'nowrap' }}
          >
            Create Dome
          </button>
        </form>
      </div>

      {/* Domes Grid */}
      <div className="row position-relative" style={{ minHeight: '200px' }}>
        {isSearching ? (
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Searching...</span>
            </div>
            <p className="mt-2 text-secondary">Searching domes...</p>
          </div>
        ) : domes?.length === 0 ? (
          <div className="col-12 text-center text-gray-500 py-5 fs-14">No domes found</div>
        ) : (
          domes?.map((dome) => (
            <div key={dome._id} className="col-xl-4 col-lg-6 col-md-6 mb-3">
              <div
                className={`card mb-3 shadow-sm hover-shadow ${isDark ? "bg-dark text-white" : ""
                  }`}
                style={{ cursor: "pointer" }}
                onClick={() => handleDomeClick(dome._id)}
              >
                {/* Dome Image */}
                {dome.coverImage ? (
                  <img
                    src={dome.coverImage}
                    alt={dome.domeName}
                    className="card-img-top"
                    style={{ height: "180px", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="bg-secondary d-flex align-items-center justify-content-center"
                    style={{ height: "180px" }}
                  >
                    <i className="fas fa-cube text-white fs-36"></i>
                  </div>
                )}

                {/* Dome Details */}
                <div className="card-body">
                  <h5 className="card-title text-primary mb-2">
                    {dome.domeName}
                  </h5>
                  <p className="text-secondary mb-1">{dome.domeDescription}</p>
                  <p className="mb-1">
                    <strong>Location:</strong> {dome.location || "N/A"}
                  </p>
                  <p className="mb-1">
                    <strong>Price:</strong> ${dome.pricePerSession || 0}
                    {/* |{" "} */}
                    {/* <strong>Offer:</strong> {dome.offerPercent}% */}
                  </p>
                  <p className="mb-1">
                    <strong>Created:</strong>{" "}
                    {dome.createdAt
                      ? new Date(dome.createdAt).toLocaleDateString()
                      : "N/A"}
                  </p>
                  <p
                    className={`mb-0 ${dome.archived ? "text-danger" : "text-success"
                      }`}
                  >
                    <strong>Status:</strong>{" "}
                    {dome.archived ? "Archived" : "Active"}
                  </p>

                  {/* Logout Tablet Button - Only show if a tablet is registered */}
                  {dome.isTabletRegistered && (
                    <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                      <button
                        className="btn btn-outline-danger btn-sm w-100"
                        onClick={(e) => handleLogoutTablet(e, dome._id, dome.domeName)}
                      >
                        <i className="fas fa-sign-out-alt me-1"></i> Logout Tablet
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {hasMore && (
        <div className="d-flex justify-content-center gap-2 my-3">
          <button
            onClick={loadNextPage}
            disabled={isLoadingMore}
            className="btn btn-primary"
          >
            {isLoadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
};

export default HostDomes;
